"use client";
import { createContext, useContext, useMemo, useState, ReactNode, FC } from "react";
import { fetchShopListing } from "graphql/queries/search/stores/store-categories";
import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
import { useQuery } from "@stadium/services/apollo-client";

interface InstantSearchProps {
  children: ReactNode;
  categoryId?: string;
  isSearch?: boolean;
}

export const SearchContext = createContext(null);

export const useInstantSearch = () => useContext(SearchContext);

const defaultArr = [];
const PER_PAGE = 1000;

const InstantSearchWrapper: FC<InstantSearchProps> = ({ categoryId, isSearch, children }) => {
  const {
    currentUserData: { accounts = defaultArr },
    loading: userLoading,
  } = useCurrentUserContext();

  const [marketingCategoryId, setMarketingCategoryId] = useState();
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");

  const accountIds = useMemo(() => accounts?.map(({ id }) => Number(id)), [accounts]);
  const marketingCategoryIdToBeFetched = categoryId || marketingCategoryId;

  const variables = useMemo(() => {
    return {
      perPage: PER_PAGE,
      marketingCategoryId: Number(marketingCategoryIdToBeFetched),
    };
  }, [marketingCategoryIdToBeFetched]);

  const searchVariables = useMemo(
    () => ({ perPage: PER_PAGE, templateSearchQuery }),
    [templateSearchQuery],
  );

  const shouldSkipDueToSearch =
    (isSearch || !marketingCategoryIdToBeFetched) && !templateSearchQuery;
  const shouldSkipDueToAccountId = accountIds.includes(Number(marketingCategoryIdToBeFetched));

  const shouldSkipQuery = shouldSkipDueToSearch || shouldSkipDueToAccountId;

  const { data, loading } = useQuery(fetchShopListing, {
    fetchPolicy: "cache-and-network",
    variables: isSearch || templateSearchQuery ? searchVariables : variables,
    skip: shouldSkipQuery,
  });

  const value = useMemo(
    () => ({
      hits: isSearch && !templateSearchQuery ? defaultArr : data?.storeSearch || defaultArr,
      loading: loading || userLoading,
      setMarketingCategoryId,
      setTemplateSearchQuery,
      templateSearchQuery,
    }),
    [data?.storeSearch, isSearch, loading, templateSearchQuery, userLoading],
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export default InstantSearchWrapper;
